import { fetchAuthSession } from 'aws-amplify/auth'
import type {
  Workspace,
  WorkspaceListItem,
  ParseResult,
  ExportResult,
  ValidationResult
} from '../types'

const API_URL = import.meta.env.VITE_API_URL || ''

async function getAuthHeaders(): Promise<HeadersInit> {
  try {
    const session = await fetchAuthSession()
    const token = session.tokens?.idToken?.toString()
    return {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` })
    }
  } catch {
    return { 'Content-Type': 'application/json' }
  }
}

async function apiRequest<T>(
  path: string,
  options: RequestInit = {}
): Promise<T> {
  const headers = await getAuthHeaders()
  const response = await fetch(`${API_URL}${path}`, {
    ...options,
    headers: { ...headers, ...options.headers }
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }))
    throw new Error(error.error || `HTTP ${response.status}`)
  }

  if (response.status === 204) {
    return {} as T
  }

  return response.json()
}

// Parse DSL (no auth required)
export async function parseDsl(dsl: string): Promise<ParseResult> {
  return apiRequest<ParseResult>('/parse', {
    method: 'POST',
    body: dsl,
    headers: { 'Content-Type': 'text/plain' }
  })
}

// Workspace CRUD
export async function createWorkspace(name: string, dsl?: string): Promise<{ workspaceId: string }> {
  return apiRequest('/workspaces', {
    method: 'POST',
    body: JSON.stringify({ name, dsl })
  })
}

export async function listWorkspaces(): Promise<{ workspaces: WorkspaceListItem[] }> {
  return apiRequest('/workspaces')
}

export async function getWorkspace(id: string): Promise<Workspace> {
  return apiRequest(`/workspaces/${id}`)
}

export async function updateWorkspace(
  id: string,
  data: { name?: string; dsl?: string }
): Promise<{ updated: boolean }> {
  return apiRequest(`/workspaces/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data)
  })
}

export async function deleteWorkspace(id: string): Promise<void> {
  return apiRequest(`/workspaces/${id}`, { method: 'DELETE' })
}

// Validation
export async function validateWorkspace(
  id: string,
  workspaceJson: string
): Promise<ValidationResult> {
  return apiRequest(`/workspaces/${id}/validate`, {
    method: 'POST',
    body: workspaceJson
  })
}

// Export
export async function exportWorkspace(
  id: string,
  format: string,
  workspaceJson: string
): Promise<ExportResult> {
  return apiRequest(`/workspaces/${id}/export/${format}`, {
    method: 'POST',
    body: workspaceJson
  })
}
